/**
 * Section Frais — taux Dingo et règlements des propriétaires (administrateurs).
 * GET/PUT /api/admin/fees, GET /api/admin/fees/settlements,
 * GET/POST /api/admin/fees/settlements/:ownerId.
 *
 * Chaque frais vaut « % du loyer, au moins un minimum » ; les deux à 0, il ne
 * coûte rien et l'app l'affiche barré. Les taux sont figés sur une location à
 * sa création : un changement ne touche que les demandes suivantes.
 */

// owner_fee_share : part des frais locataires qui revient au propriétaire, en
// pourcentage seul (le minimum reste à 0). Elle vient en déduction de son dû.
export type FeeCode = 'renter_service' | 'renter_protection' | 'owner_commission' | 'owner_fee_share';
export interface FeeRule { percent: number; min_amount: number }
export type PlatformFees = Record<FeeCode, FeeRule>;

export interface OwnerBalance {
  owner_id: string; name: string; email: string | null; phone: string | null; profile_photo?: string | null;
  rentals_count: number; owed: number; settled: number; balance: number; last_settlement_at: string | null;
  /** Au-dessus du seuil de rappel depuis plus de 7 jours (relance à faire). */
  overdue: boolean;
  /** Annonces hors ligne pour solde impayé. */
  blocked: boolean;
}

export interface EarningsLine {
  id_rental: string; completed_at: string | null; vehicle_label: string | null; renter_name: string | null;
  rental_price: number; service_fee: number; protection_fee: number; owner_commission: number; owner_fee_share?: number; net: number; owed: number;
}

export interface OwnerEarnings {
  summary: {
    rentals_count: number; gross: number; commission: number; net: number;
    renter_fees_collected: number; renter_fees_share?: number; owed_total: number; settled_total: number; balance: number;
  };
  rentals: EarningsLine[];
  settlements: { id_settlement: string; amount: number; note: string | null; created_at: string }[];
}

const base = () => `${process.env.REACT_APP_API_URL}api/admin/fees`;

async function call<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${base()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers || {}) }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data as T;
}

export const getFees = async (token: string): Promise<PlatformFees> =>
  (await call<{ fees: PlatformFees }>(token, '')).fees;

export const updateFees = async (token: string, fees: PlatformFees): Promise<PlatformFees> =>
  (await call<{ fees: PlatformFees }>(token, '', { method: 'PUT', body: JSON.stringify({ fees }) })).fees;

/** Propriétaires en retard ou hors ligne : pastille sur « Frais ». */
export const getOverdueCount = async (token: string): Promise<number> =>
  (await call<{ count: number }>(token, '/overdue-count')).count;

export interface RecentSettlement {
  id_settlement: string; owner_id: string; name: string; profile_photo: string | null;
  amount: number; note: string | null; recorded_by_name: string | null; created_at: string;
}

/** Soldes, plus le mois en cours (heure de Tunis) et les derniers règlements reçus. */
export interface BalancesOverview {
  owners: OwnerBalance[];
  month?: { settled: number; settled_previous: number; completed_rentals: number };
  recent?: RecentSettlement[];
}

export const getBalances = async (token: string): Promise<BalancesOverview> =>
  call<BalancesOverview>(token, '/settlements');

export const getOwnerDetail = async (token: string, ownerId: string): Promise<OwnerEarnings> =>
  call<OwnerEarnings>(token, `/settlements/${ownerId}`);

export const recordSettlement = async (token: string, ownerId: string, amount: number, note: string): Promise<OwnerEarnings> =>
  call<OwnerEarnings>(token, `/settlements/${ownerId}`, { method: 'POST', body: JSON.stringify({ amount, note }) });

/** Seuils des relances et de la mise hors ligne (Frais › Configuration). */
export interface FeesDueSettings {
  remindThreshold: number;
  remindEveryDays: number;
  overdueDays: number;
  blockAmount: number;
  blockAfterDays: number;
}

export const getFeesDueSettings = async (token: string): Promise<FeesDueSettings> =>
  (await call<{ settings: FeesDueSettings }>(token, '/settings')).settings;

export const updateFeesDueSettings = async (token: string, settings: FeesDueSettings): Promise<FeesDueSettings> =>
  (await call<{ settings: FeesDueSettings }>(token, '/settings', { method: 'PUT', body: JSON.stringify({ settings }) })).settings;
